:host {
  display: block;
  box-sizing: border-box;
  /* Query container: the banner adapts to its OWN width, not the viewport, so
     it lays out correctly in any container (narrow column, Storybook Docs
     canvas, real bottom-sheet) — see the @container block below. */
  container-type: inline-size;
  inline-size: 100%;
  max-inline-size: var(--cookie-banner-container-max-width);
  min-inline-size: 0;
  font-family: var(--cookie-banner-body-font-family);
  color: var(--cookie-banner-title-color);

  z-index: var(--cookie-banner-container-stack-z-index);

  /* Allow consumers to position with their own layout; default is in-flow.
   * For fixed-to-viewport positioning consumers wrap the element in a
   * positioning container or apply `position: fixed; inset-block-end: 16px;`
   * via CSS.
   */
}

.container {
  display: flex;
  flex-direction: column;
  inline-size: 100%;
  box-sizing: border-box;
  background-color: var(--cookie-banner-container-background);
  border: var(--cookie-banner-container-border-width) solid var(--cookie-banner-container-border);
  border-radius: var(--cookie-banner-container-border-radius);
  box-shadow: var(--cookie-banner-container-shadow-small);
  overflow: hidden;
}

/* ---------- Header ---------- */

.header {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: var(--cookie-banner-header-gap);
  padding-block-start: var(--cookie-banner-header-padding-top);
  padding-block-end: var(--cookie-banner-header-padding-bottom);
  padding-inline-start: var(--cookie-banner-container-padding-inline-desktop);
  padding-inline-end: var(--spacing-20, 20px);
}

.heading {
  flex: 1 1 0;
  min-inline-size: 0;
  display: flex;
  align-items: center;
}

.title {
  flex: 1 1 0;
  min-inline-size: 0;
  margin: 0;
  font-family: var(--cookie-banner-title-font-family);
  font-size: var(--cookie-banner-title-font-size);
  line-height: var(--cookie-banner-title-line-height);
  font-weight: var(--cookie-banner-title-font-weight);
  letter-spacing: var(--cookie-banner-title-letter-spacing);
  color: var(--cookie-banner-title-color);
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---------- Close button (expanded only) ---------- */

.close {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--cookie-banner-close-size);
  block-size: var(--cookie-banner-close-size);
  padding: 0;
  margin: 0;
  border: 0;
  border-radius: var(--cookie-banner-close-border-radius);
  background-color: var(--cookie-banner-close-background);
  color: var(--cookie-banner-close-color);
  cursor: pointer;
  transition:
    background-color 120ms ease-out,
    color 120ms ease-out;
}

.close mud-icon {
  --icon-color: currentcolor;
  inline-size: var(--cookie-banner-close-icon-size);
  block-size: var(--cookie-banner-close-icon-size);
}

.close:hover {
  background-color: var(--cookie-banner-close-background-hover);
}

.close:focus-visible {
  outline: var(--focus-ring-width, 2px) solid var(--focus-ring-color, var(--color-border-brand-default));
  outline-offset: 2px;
}

/* ---------- Body ---------- */

.body {
  display: flex;
  flex-direction: column;
  gap: var(--cookie-banner-body-gap);
  padding-block-start: var(--cookie-banner-body-padding-top);
  padding-block-end: var(--cookie-banner-body-padding-bottom);
  padding-inline: var(--cookie-banner-container-padding-inline-desktop);
}

:host(.is-expanded) .body {
  gap: var(--cookie-banner-body-gap-expanded);
}

.body-text {
  margin: 0;
  font-family: var(--cookie-banner-body-font-family);
  font-size: var(--cookie-banner-body-font-size);
  line-height: var(--cookie-banner-body-line-height);
  font-weight: var(--cookie-banner-body-font-weight);
  color: var(--cookie-banner-body-color);
  overflow-wrap: anywhere;
}

.privacy-wrapper {
  display: inline;
}

::slotted([slot='body']) {
  color: var(--cookie-banner-body-color);
  font-family: var(--cookie-banner-body-font-family);
  font-size: var(--cookie-banner-body-font-size);
  line-height: var(--cookie-banner-body-line-height);
}

/* ---------- Categories ---------- */

.categories {
  display: flex;
  flex-direction: column;
  gap: var(--cookie-banner-categories-row-gap);
  margin-inline: var(--cookie-banner-container-padding-inline-desktop);
  margin-block-end: var(--cookie-banner-body-padding-bottom);
  padding-inline: var(--cookie-banner-categories-padding-inline);
  padding-block-start: var(--cookie-banner-categories-padding-top);
  padding-block-end: var(--cookie-banner-categories-padding-bottom);
  background-color: var(--cookie-banner-categories-background);
  border-radius: var(--cookie-banner-categories-border-radius);
}

::slotted([slot='categories']) {
  color: inherit;
}

.category {
  display: flex;
  align-items: center;
  gap: var(--cookie-banner-category-gap);
  padding-block: var(--cookie-banner-category-padding-block);
}

.category-text {
  flex: 1 1 0;
  min-inline-size: 0;
  display: flex;
  flex-direction: column;
  gap: var(--cookie-banner-category-row-gap);
}

.category-heading {
  display: flex;
  align-items: center;
  gap: var(--cookie-banner-category-header-gap);
}

.category-label {
  /* Allow the label to wrap so the "Required" tag never gets compressed/clipped. */
  min-inline-size: 0;
  font-family: var(--cookie-banner-category-label-font-family);
  font-size: var(--cookie-banner-category-label-font-size);
  line-height: var(--cookie-banner-category-label-line-height);
  font-weight: var(--cookie-banner-category-label-font-weight);
  color: var(--cookie-banner-category-label-color);
}

/* Keep the required tag at its intrinsic width — it must not shrink/clip. */
.category-heading mud-tag {
  flex-shrink: 0;
}

.category-description {
  margin: 0;
  font-family: var(--cookie-banner-category-description-font-family);
  font-size: var(--cookie-banner-category-description-font-size);
  line-height: var(--cookie-banner-category-description-line-height);
  font-weight: var(--cookie-banner-category-description-font-weight);
  color: var(--cookie-banner-category-description-color);
  overflow-wrap: anywhere;
}

/* Mobile: clamp the description to 2 lines until the user expands it (per the
   Figma `mobile: extended` → `text-extended` states). Applied via the
   `is-clamped` class, which is only added on the mobile breakpoint. */
.category-description.is-clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

/* "Mai mult / Mai puțin" (more/less) toggle — a subtle inline text affordance,
   shown only on mobile when the description overflows its clamp. */
.category-description-toggle {
  align-self: flex-start;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  font-family: var(--cookie-banner-category-description-font-family);
  font-size: var(--cookie-banner-category-description-font-size);
  line-height: var(--cookie-banner-category-description-line-height);
  font-weight: var(--cookie-banner-category-label-font-weight);
  color: var(--cookie-banner-link-color, var(--color-text-brand-default));
  cursor: pointer;
  text-align: start;
}

.category-description-toggle:hover {
  text-decoration: underline;
}

.category-description-toggle:focus-visible {
  outline: var(--focus-ring-width, 2px) solid var(--focus-ring-color, var(--color-border-brand-default));
  outline-offset: 2px;
  border-radius: var(--border-radius-4, 4px);
}

.category-required-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 48px;
  block-size: 48px;
  color: var(--cookie-banner-category-required-icon-color);
}

.category-required-icon mud-icon {
  --icon-color: currentcolor;
  inline-size: var(--cookie-banner-category-required-icon-size);
  block-size: var(--cookie-banner-category-required-icon-size);
}

/* ---------- Footer ---------- */

.footer {
  display: flex;
  align-items: center;
  gap: var(--cookie-banner-footer-gap);
  padding-block-start: var(--cookie-banner-footer-padding-top);
  padding-block-end: var(--cookie-banner-footer-padding-bottom);
  padding-inline: var(--cookie-banner-container-padding-inline-desktop);
}

.footer--collapsed {
  justify-content: space-between;
}

.footer--collapsed .cta-group {
  display: flex;
  align-items: center;
  gap: var(--cookie-banner-footer-gap);
  /* Figma: the reject/accept pair live in a fixed 414px group, each button
     flex-1 (≈201px) and full-width so the control fills its slot — no dead space. */
  inline-size: 414px;
  flex-shrink: 0;
}

.footer--collapsed .cta-group mud-button {
  flex: 1 1 0;
  min-inline-size: 0;
}

.footer--expanded {
  justify-content: flex-end;
}

.footer--expanded mud-button {
  inline-size: 201px;
}

/* ---------- Position attribute (host-driven) ---------- */

:host([position='top']) .container {
  /* Inherit the same shadow; consumers control positioning via inset rules. */
}

/* ---------- Mobile breakpoint (container-driven) ----------
   Triggered by the banner's OWN inline-size (≤540px), not the viewport — so the
   mobile layout renders in any narrow container, including the Storybook Docs
   canvas. The JS button-size swap (md→lg) is driven by the same threshold via a
   ResizeObserver. */

@container (max-width: 540px) {
  .container {
    border-radius: var(--cookie-banner-container-border-radius-mobile);
  }

  .header {
    padding-block-start: var(--cookie-banner-header-padding-top-mobile);
    padding-inline-start: var(--cookie-banner-container-padding-inline-mobile);
    padding-inline-end: var(--cookie-banner-header-padding-inline-end-mobile);
  }

  /* Responsive typography — mobile scales up (H3 title, Body/Default). */
  .title {
    font-size: var(--cookie-banner-title-font-size-mobile);
    line-height: var(--cookie-banner-title-line-height-mobile);
    letter-spacing: var(--cookie-banner-title-letter-spacing-mobile);
  }

  .body {
    padding-block-start: var(--cookie-banner-body-padding-top-mobile);
    padding-inline: var(--cookie-banner-container-padding-inline-mobile);
  }

  .body-text,
  ::slotted([slot='body']) {
    font-size: var(--cookie-banner-body-font-size-mobile);
    line-height: var(--cookie-banner-body-line-height-mobile);
  }

  .footer {
    padding-inline: var(--cookie-banner-container-padding-inline-mobile);
    padding-block-end: var(--cookie-banner-footer-padding-bottom-mobile);
    flex-direction: column;
    align-items: stretch;
  }

  .footer--collapsed {
    gap: var(--spacing-8, 8px);
  }

  /* Mobile stack order (Figma): Accept (top) → Reject → Manage (bottom). */
  .footer--collapsed .cta-manage {
    order: 1;
  }

  .footer--collapsed .cta-group {
    flex-direction: column-reverse;
    inline-size: 100%;
    gap: var(--spacing-8, 8px);
  }

  /* Reset the desktop flex-fill — buttons are full-width-stacked on mobile. */
  .footer--collapsed .cta-group mud-button {
    flex: 0 0 auto;
  }

  .footer--collapsed mud-button {
    inline-size: 100%;
    min-inline-size: 0;
  }

  .footer--expanded mud-button {
    inline-size: 100%;
    min-inline-size: 0;
  }

  .categories {
    margin-inline: var(--cookie-banner-container-padding-inline-mobile);
    padding-inline: var(--spacing-16, 16px);
    gap: var(--spacing-8, 8px);
  }

  .category {
    gap: var(--cookie-banner-category-gap-mobile);
  }

  .category-required-icon,
  .category mud-switch {
    align-self: flex-start;
  }
}

/* ---------- Reduced motion ---------- */

@media (prefers-reduced-motion: reduce) {
  .close {
    transition: none;
  }
}
